import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { WorkbookV2Item } from './WorkbookV2Item';
import React, { useState } from 'react';
import { SortableList } from '../❉molecules/SortableList/SortableList';
import { AccordionItem } from '@szhsin/react-accordion';
import { ChevronDownIcon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { DragHandle } from '../❉molecules/SortableList/components/SortableItem/SortableItem';
import { TrashIcon } from '@heroicons/react/24/outline';
import { SimpleModal } from '../❉molecules/SimpleModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faImage } from '@fortawesome/free-regular-svg-icons';
import {
  faBookOpen,
  faCircleCheck,
  faHeading,
  faLink,
  faList,
  faQuoteLeft,
  faSquareCheck,
  faStar,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { faComment } from '@fortawesome/free-regular-svg-icons/faComment';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import { TranslateButton } from '../atoms/translateButton';
import { TranslateLessonButton } from '../atoms/translateLessonButton';
import { isWorkbookV2TempId } from './editorUtils';

const ITEM_TYPES = [
  { label: 'Content', value: 'content', icon: faFileLines },
  { label: 'Quote', value: 'quote', icon: faQuoteLeft },
  { label: 'Verse', value: 'verse', icon: faBookOpen },
  { label: 'Text Input', value: 'textInput', icon: faComment },
  { label: 'Checkbox', value: 'checkbox', icon: faSquareCheck },
  { label: 'Radio', value: 'radio', icon: faCircleCheck },
  { label: 'Rating', value: 'rating', icon: faStar },
  { label: 'Image', value: 'image', icon: faImage },
  { label: 'Header', value: 'header', icon: faHeading },
  { label: 'List', value: 'list', icon: faList },
  { label: 'Link', value: 'link', icon: faLink },
  { label: 'Video', value: 'inlineVideo', icon: faVideo }
];

export const WorkbookV2Lesson = ({
  workbook,
  chapter_id,
  lesson,
  deleteLesson,
  editingLanguage = null,
  onUpdateLesson,
  onCreateItem,
  onDeleteItem,
  onUploadItemImage,
  onUpdateItem,
  onLessonTranslated
}) => {
  const [showItemAdder, setShowItemAdder] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);
  const [refreshHack, setRefreshHack] = useState(0);

  const headerContent = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <DragHandle />
        <ChevronDownIcon className="size-10 duration-300"></ChevronDownIcon>
        {lesson.title}
      </div>
      <div className="flex items-center gap-8">
        {editingLanguage && (
          isWorkbookV2TempId(lesson._id) ? (
            <span className="rounded-full px-3 py-1 text-sm text-gray-400">Save to translate</span>
          ) : (
            <TranslateLessonButton
              lessonId={lesson._id}
              language={editingLanguage}
              setLesson={(translatedLesson) => {
                onLessonTranslated(chapter_id, translatedLesson);
                setRefreshHack((value) => value + 1);
              }}
            />
          )
        )}
        <TrashIcon className="text-pdred/70 size-9" onClick={() => setShowDeleteModal(true)} />
      </div>
      {showDeleteModal && (
        <SimpleModal hideModal={() => setShowDeleteModal(false)}>
          <div className="flex flex-col items-center gap-2">
            <span>Are you sure you want to delete this lesson?</span>
            <button
              className="bg-pdred rounded-2xl p-6 text-white shadow-md"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                deleteLesson(chapter_id, lesson._id);
              }}
            >
              Yes
            </button>
          </div>
        </SimpleModal>
      )}
    </div>
  );

  return (
    <AccordionItem header={headerContent} className="px-4">
      <div className={`grid gap-8 py-2 ${editingLanguage ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div className="relative">
          <OneLineInput
            value={lesson.title}
            label="Title"
            onChange={(value) => onUpdateLesson(chapter_id, lesson._id, { title: value })}
          ></OneLineInput>
          {editingLanguage && (
            <TranslateButton
              text={lesson.title}
              language={editingLanguage}
              onResponse={(value) =>
                onUpdateLesson(chapter_id, lesson._id, {
                  translations: updateTranslation(lesson.translations ?? {}, editingLanguage, 'title', value)
                })
              }
            />
          )}
        </div>
        {editingLanguage && (
          <OneLineInput
            value={getTranslation(lesson.translations, editingLanguage, 'title')}
            label={`Title (${editingLanguage})`}
            onChange={(value) =>
              onUpdateLesson(chapter_id, lesson._id, {
                translations: updateTranslation(lesson.translations ?? {}, editingLanguage, 'title', value)
              })
            }
          ></OneLineInput>
        )}
        <div className="col-span-2 flex flex-col items-start p-3">
          <SortableList
            items={lesson.items ?? []}
            onChange={(items) => onUpdateLesson(chapter_id, lesson._id, { items })}
            renderItem={(item) => (
              <SortableList.Item id={item._id} key={`${item._id}-${refreshHack}`}>
                <div className="w-full" key={item._id}>
                  <WorkbookV2Item
                    items={lesson.items ?? []}
                    item={item}
                    editor={true}
                    updateItem={(nextItem) => onUpdateItem(chapter_id, lesson._id, nextItem)}
                    deleteItem={(itemId) => onDeleteItem(chapter_id, lesson._id, itemId)}
                    uploadImage={(image) => onUploadItemImage(chapter_id, lesson._id, item._id, image)}
                    openInEdit={openItemId === item._id}
                    editingLanguage={editingLanguage}
                    workbook={workbook}
                  ></WorkbookV2Item>
                </div>
              </SortableList.Item>
            )}
          />
          {showItemAdder ? (
            <div className="flex w-1/2 flex-wrap items-center justify-center gap-2 self-center p-5">
              {ITEM_TYPES.map((type) => (
                <button
                  key={type.value}
                  className="bg-pdteal hover:bg-pddarkblue flex items-center gap-2 rounded-full p-4 text-2xl text-white shadow-md hover:cursor-pointer"
                  onClick={() => {
                    const createdItem = onCreateItem(chapter_id, lesson._id, { type: type.value, text: '' });

                    if (createdItem?._id) {
                      setOpenItemId(createdItem._id);
                    }

                    setShowItemAdder(false);
                  }}
                >
                  <FontAwesomeIcon icon={type.icon} className="size-8" />
                  {type.label}
                </button>
              ))}
            </div>
          ) : (
            <div
              className="group flex items-center self-center hover:cursor-pointer"
              onClick={() => setShowItemAdder(true)}
            >
              <PlusCircleIcon className="text-pdteal size-12 self-center drop-shadow-md hover:cursor-pointer" />
              <div className="block w-0 overflow-hidden whitespace-nowrap transition-[width] duration-500 group-hover:w-50">
                Add an Item
              </div>
            </div>
          )}
        </div>
      </div>
    </AccordionItem>
  );
};
